import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { DEFAULT_JOURNAL_DOMAIN_ROUTES } from 'src/_helpers/journalDomainRoutes'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'

export const ID_NAME_LINKS = {
  issues: {
    getRoute: (routes) => routes.ISSUES.LIST,
    labelCode: 'T_JOURNAL_ISSUES_TITLE',
  },
  ahead_of_print: {
    getRoute: (routes) => routes.JOURNALS.AHEAD_OF_PRINT,
    labelCode: 'T_JOURNAL_AHEAD_OF_PRINT_TITLE',
  },
  editorial_board: {
    getRoute: (routes) => routes.JOURNALS.EDITORIAL_BOARD,
    labelCode: 'T_JOURNAL_EDITORIAL_BOARD_TITLE',
  },
  advisory_board: {
    getRoute: (routes) => routes.JOURNALS.SCIENTIFIC_ADVISORY_BOARD,
    labelCode: 'T_JOURNAL_SCIENTIFIC_ADVISORY_BOARD_TITLE',
  },
  our_reviewers: {
    getRoute: (routes) => routes.JOURNALS.OUR_REVIEWERS,
    labelCode: 'T_JOURNAL_REVIEWERS_TITLE',
  },
}

const normalizeCollection = (value) => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.['hydra:member'])) return value['hydra:member']
  if (value && typeof value === 'object') return Object.values(value)
  return []
}

const sortByOrd = (items, direction = 'asc') =>
  normalizeCollection(items)
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const aOrd = Number.isFinite(Number(a.item?.ord)) ? Number(a.item.ord) : 0
      const bOrd = Number.isFinite(Number(b.item?.ord)) ? Number(b.item.ord) : 0
      const order = direction === 'desc' ? bOrd - aOrd : aOrd - bOrd

      return order || a.index - b.index
    })
    .map(({ item }) => item)

const getPageDataTranslation = (item, locale) => {
  const translations = item?.pageData?.translations || {}

  return translations[locale] || translations.pl || translations.en || Object.values(translations)[0] || {}
}

const defaultTranslate = (code) => code

const getIdNameLink = (item, locale, translate = defaultTranslate, routes = DEFAULT_JOURNAL_DOMAIN_ROUTES) => {
  const config = ID_NAME_LINKS[item?.idName]

  if (!config) {
    return null
  }

  const href = buildLocalizedPath(config.getRoute(routes), locale)
  const label = translate(config.labelCode)

  return href && label ? [label, href] : null
}

const getMenuItemLinks = (
  items,
  locale,
  { sortDirection = null, translate = defaultTranslate, routes = DEFAULT_JOURNAL_DOMAIN_ROUTES } = {},
) =>
  (sortDirection ? sortByOrd(items, sortDirection) : normalizeCollection(items))
    .flatMap((item) => {
      if (item?.stat === false) {
        return []
      }

      const idNameLink = getIdNameLink(item, locale, translate, routes)
      if (idNameLink) {
        return [idNameLink, ...getMenuItemLinks(item.children, locale, { sortDirection, translate, routes })]
      }

      const translation = getPreferredTranslation(item, locale) || {}
      const pageTranslation = getPageDataTranslation(item, locale)
      const title = (translation.title || pageTranslation.title || '').trim()
      const href =
        translation.link ||
        (pageTranslation.slug
          ? buildLocalizedPath(routes.JOURNALS.SUBPAGE, locale, { slug: pageTranslation.slug })
          : null)
      const link = title && href ? [[title, href]] : []

      return [...link, ...getMenuItemLinks(item.children, locale, { sortDirection, translate, routes })]
    })
    .filter(Boolean)

const buildNavTree = (
  items,
  locale,
  sortDirection = 'desc',
  translate = defaultTranslate,
  routes = DEFAULT_JOURNAL_DOMAIN_ROUTES,
) =>
  sortByOrd(items, sortDirection)
    .map((item) => {
      if (item?.stat === false) {
        return null
      }

      const idNameLink = getIdNameLink(item, locale, translate, routes)
      let label = null
      let href = null

      if (idNameLink) {
        label = idNameLink[0]
        href = idNameLink[1]
      } else {
        const translation = getPreferredTranslation(item, locale) || {}
        const pageTranslation = getPageDataTranslation(item, locale)

        label = (translation.title || pageTranslation.title || '').trim() || null
        href =
          translation.link ||
          (pageTranslation.slug
            ? buildLocalizedPath(routes.JOURNALS.SUBPAGE, locale, { slug: pageTranslation.slug })
            : null)
      }

      const children = buildNavTree(item?.children, locale, sortDirection, translate, routes)

      if (!label && children.length === 0) {
        return null
      }

      return { label, href: href || null, children }
    })
    .filter(Boolean)

const getFooterItemColumns = (items, locale, translate = defaultTranslate, routes = DEFAULT_JOURNAL_DOMAIN_ROUTES) => {
  const columns = [[], []]

  sortByOrd(items, 'asc').forEach((item) => {
    const columnIndex = Number(item?.columnId) === 3 ? 1 : 0

    columns[columnIndex].push(...getMenuItemLinks([item], locale, { sortDirection: 'asc', translate, routes }))
  })

  return columns
}

export const getDomainJournalNavLinks = (journal, locale, translate = defaultTranslate, routes) => [
  ...getMenuItemLinks(journal?.menuItems || journal?.menu, locale, { sortDirection: 'desc', translate, routes }),
]

export const getDomainJournalNavTree = (journal, locale, translate = defaultTranslate, routes) =>
  buildNavTree(journal?.menuItems || journal?.menu, locale, 'desc', translate, routes)

export const getDomainJournalFooterLinks = (journal, locale, translate = defaultTranslate, routes) => [
  ...getMenuItemLinks(journal?.footerItems || journal?.footer, locale, { sortDirection: 'asc', translate, routes }),
]

export const getDomainJournalFooterColumns = (journal, locale, translate = defaultTranslate, routes) => {
  const [firstColumnItems, secondColumnItems] = getFooterItemColumns(
    journal?.footerItems || journal?.footer,
    locale,
    translate,
    routes,
  )

  return [firstColumnItems, secondColumnItems]
}
